/**
 * ParticleCloudCanvas — Multi-layer animated particle flow field
 *
 * Framework-free canvas engine. Each layer runs an independent simulation
 * (particles, loci, density grid, flow field) but all layers draw to a
 * single shared canvas for efficiency — one clear, one rAF loop.
 */


// ————————————————————————————————————————————————————————————————————
// Per-layer defaults
// ————————————————————————————————————————————————————————————————————

export const LAYER_DEFAULTS = {

	// ── General ─────────────────────────────────────────────────────────

	// Fill color for every particle (any valid CSS color)
	color: '#A2BCE7',

	// Layer scale (0-1) — multiplier of the canvas radius. Controls both
	// the simulation radius (where particles live) and the particle count
	// (linear with scale). All layers share the canvas center, so smaller
	// scales render concentrically inside larger ones.
	scale: 1,

	// Override the auto-derived particle count. When null, count is
	// computed from the layer's pixel diameter (see _layerCount): a
	// 1000 px scale-1 layer yields ~1000 particles, scaling linearly.
	particleCount: null,

	// Multiplier applied to the auto-derived particle count when
	// particleCount is null. 1 = default density, 0.5 = half, 2 = double.
	// Ignored when particleCount is set.
	densityMultiplier: 1,

	// Global speed multiplier applied to baseVelocity.
	speed: 1,

	// Base movement speed per frame (in CSS px) before the speed multiplier.
	baseVelocity: 0.05,

	// Smallest particle radius (CSS px). Used in low-density regions.
	minRadius: 1.5,

	// Largest particle radius (CSS px). Used in high-density regions.
	maxRadius: 6,

	// ── Flow field (pseudo-noise) ───────────────────────────────────────

	// Spatial frequency of the flow field. Smaller = broader, smoother
	// currents; larger = tighter, more chaotic swirls.
	noiseScale: 0.003,

	// How fast the flow field drifts over time. Controls the rate of
	// change for the global cos(t * noiseSpeed) term — higher values
	// make the field shift more quickly, creating restless motion.
	noiseSpeed: 0.0002,

	// ── Loci (spontaneous attraction / repulsion points) ────────────────
	// Loci spawn continuously, travel along an ellipse arc, fade in then
	// out, and die. New loci replace expired ones to maintain the count.

	// Types available and their relative weights for random spawning.
	// Set weight to 0 to disable a type entirely.
	lociWeights: [
		{ type: 'attract', weight: 1 },
		{ type: 'repel', weight: 1 },
		{ type: 'orbit', weight: 2 },
		{ type: 'dampen', weight: 1 },
	],

	// Number of loci alive at any given time. As one expires, a new
	// one spawns to replace it.
	locusCount: 12,

	// Lifespan range (seconds). Each locus picks a random lifespan
	// within this range. Strength fades 0 → max → 0 over the life.
	locusMinLife: 3,
	locusMaxLife: 7,

	// Min/max influence radius as a fraction of the circle radius (0-1).
	locusMinInfluence: 0.2,
	locusMaxInfluence: 0.7,

	// Peak strength each locus can reach (randomized per locus).
	locusMinStrength: 0.02,
	locusMaxStrength: 0.04,

	// Multiplier applied to 'dampen' loci strength.
	dampenStrengthScale: 0.5,

	// ── Locus arc geometry ──────────────────────────────────────────
	// Each locus travels along a segment of an ellipse during its life.

	// Semi-major axis range as a fraction of the circle radius.
	locusArcMinRadius: 0.1,
	locusArcMaxRadius: 0.5,

	// Eccentricity: semi-minor as a fraction of semi-major.
	// 1.0 = circular arc, 0.1 = very elongated.
	locusArcMinEccentricity: 0.4,
	locusArcMaxEccentricity: 1.0,

	// How far the arc center sits from the field center (0-1 of radius).
	locusArcMinOffset: 0,
	locusArcMaxOffset: 0.6,

	// Arc sweep: how many radians the locus travels over its life.
	// π/4 ≈ 45°, π/2 ≈ 90°, π ≈ 180°.
	locusArcMinSweep: Math.PI / 6,
	locusArcMaxSweep: Math.PI / 2,

	// ── Locus edge protection ───────────────────────────────────────────
	// Spontaneous loci lose some influence near the circle edge so they
	// can't drag particles into unnatural edge clumps — but they keep a
	// configurable floor of strength so edge particles still feel a nudge.

	// Distance from center (as fraction of radius, 0-1) where locus
	// influence begins to fade. 0.85 = full strength inside 85% of the
	// radius, then linear drop toward locusEdgeFalloffMin at the edge.
	locusEdgeFalloffStart: 0.85,

	// Fraction of full strength that loci retain at the circle edge,
	// for particles still INSIDE the circle. Once a particle drifts
	// past the edge it is committed to fading out — loci no longer
	// influence it regardless of this value. 0 reproduces the old
	// "drops to zero at the edge" behavior; 0.05 = loci keep 5%
	// strength at the edge so edge particles still feel a nudge.
	locusEdgeFalloffMin: 0.05,

	// ── Density grid ────────────────────────────────────────────────────
	// A coarse grid bins particles to estimate local density. Denser
	// regions get larger particles, creating an organic clustering look.

	// Number of grid cells per axis (total cells = densityGridCells²).
	// Higher = finer resolution but more overhead per update.
	densityGridCells: 20,

	// Recalculate the density grid every N frames. Lower = more
	// responsive size changes but higher per-frame cost.
	densityUpdateInterval: 10,

	// How quickly particle radii lerp toward their density target
	// per second. Higher = snappier, lower = smoother. 3 gives a
	// smooth ~0.3s transition.
	radiusLerpSpeed: 3,

	// Density thresholds (particle count per cell). Cells at or below
	// densityMin map to minRadius; cells at or above densityMax map to
	// maxRadius. Values between are linearly interpolated.
	densityMin: 2,
	densityMax: 15,

	// ── Particle separation ─────────────────────────────────────────
	// Short-range repulsion between nearby particles. Uses the density
	// grid for spatial lookup so cost is O(n) not O(n²).

	// Distance (CSS px) within which particles repel each other.
	// 0 = disabled. Good starting range: 8-15.
	separationRadius: 4.5,

	// Strength of the repulsion force. Higher = stronger push apart.
	separationStrength: 0.04,

	// ── Boundary forces ─────────────────────────────────────────────────
	// Keep particles inside the circular boundary with soft inward push
	// rather than hard clipping.

	// Distance from center (as fraction of radius, 0-1) where the
	// inward boundary force begins. 0.92 = force kicks in at 92% of
	// the radius, letting particles spread closer to the edge.
	boundaryForceStart: 0.95,

	// Strength of the inward boundary push. 0 = disabled (default; the
	// circle is now a soft fade boundary, not a wall). Higher = harder
	// wall. Force grows quadratically past boundaryForceStart.
	boundaryForceStrength: 0,

	// Outer radius of the band where destroyed particles respawn, as a
	// fraction of the layer radius. 0.20 = within 20% of the radius from
	// center, i.e. inside a circle of diameter 20% of the parent's diameter.
	// Paired with respawnInnerRadiusFraction (default 0) this is the outer
	// edge of the respawn annulus.
	respawnOuterRadiusFraction: 0.20,

	// Fraction of the radius defining a central VOID where particles never
	// spawn — neither on the initial seed nor on respawn. Spawning happens
	// uniformly in the annulus between this inner radius and
	// respawnOuterRadiusFraction. 0 = no void (the whole inner disc is seeded,
	// original behavior). Pair with a positive centerRepelStrength so drifting
	// particles can't wander back into the void: respawn keeps them out, repel
	// keeps them out. Set this to roughly an inner layer's scale so a larger
	// layer holds a clear hole around the smaller one. Must be <
	// respawnOuterRadiusFraction; if it isn't, the void radius is used for both.
	respawnInnerRadiusFraction: 0,

	// Seconds for a particle to fade fully in or out. Applies when a
	// particle crosses the circle boundary in either direction and when
	// a respawned particle fades in from the center. Independent of how
	// far past the edge the particle is.
	particleFadeDuration: 0.5,

	// ── Edge fade (artificial inner edge) ────────────────────────────
	// Width (CSS px) of a fade band just inside the true edge. This creates
	// an artificial edge *inside* the circular clip mask: a particle's drawn
	// size scales geometrically from full at edgeFadeWidth px in from the
	// edge to zero at the edge itself, so it is always invisible by the time
	// it reaches the mask — you never see a particle cropped mid-body at the
	// boundary. A fixed px band keeps the fade visually consistent across
	// cloud sizes (unlike a fraction of the radius, which scales with the
	// cloud). Because it is distance- (not time-) based, it holds regardless
	// of particle speed. 0 disables the fade (hard crop at the edge).
	edgeFadeWidth: 12,

	// ── Intro reveal ────────────────────────────────────────────────
	// On load (and on reset/replay) every particle starts hidden and
	// fades in after a random per-particle delay, so the field assembles
	// in a scattered order instead of snapping into view mid-motion.
	// Particles still move while hidden, so by the time each one fades
	// in the flow is already underway — no frozen initial state shows.
	// Max stagger before a particle begins its fade (seconds). Each
	// particle still fades in quickly (over particleFadeDuration); this
	// spreads the *start* of those fades across a wider window so the
	// full field takes longer to assemble. The last particle finishes
	// at introMaxDelay + particleFadeDuration ≈ 3s here.
	introMaxDelay: 2.5,

	// Outward push from the center to drive particles toward the edges.
	// Higher values create a more pronounced hollow center / ring effect.
	// centerRepelStrength: 0.004,
	centerRepelStrength: 0.0,

	// Radius (as fraction of circle radius) within which the center
	// repulsion applies. 0.8 = inner 80% of the circle feels a soft
	// outward nudge, concentrating particles into the outer band.
	centerRepelFalloff: 1.5,

	// ── Center orbit force ──────────────────────────────────────────
	// Tangential force around the center, making particles orbit.
	// Linear strength scales with distance from the center across the
	// inner plateau (centerOrbitPlateau, as a fraction of the falloff
	// radius) — this keeps angular velocity uniform across the plateau,
	// otherwise particles near the exact center would spin arbitrarily
	// fast. Past the plateau, strength fades quadratically to zero by
	// the falloff edge. Independent of centerRepelStrength.
	centerOrbitStrength: 0.02,

	// Radius (as fraction of circle radius) within which the center
	// orbit force applies. Particles beyond this feel no orbit pull.
	centerOrbitFalloff: 2.0,

	// Fraction (0–1) of the centerOrbitFalloff radius across which
	// orbit strength ramps up linearly with distance (uniform angular
	// velocity). Outside this fraction, strength fades quadratically
	// to zero at the falloff edge. 1.0 = no fade-out, pure ramp;
	// 0 = no plateau, pure falloff from center.
	centerOrbitPlateau: 0.7,
};


// Paste tweaks here (e.g. straight from the demo's "Show JS" output) and they
// become the effective defaults for every cloud on the page. The demo sliders
// also start at these values, so Reset returns here — not to the values in
// LAYER_DEFAULTS above. LAYER_DEFAULTS is left pristine so the demo can show
// the full diff against framework defaults, including this object's tweaks.
export const LAYER_OVERRIDES = {
	densityMultiplier: 2.4,
	speed: 3,
	locusCount: 6,
	locusMinLife: 2,
	locusMaxLife: 4,
	locusMinInfluence: 0.4,
	locusMaxInfluence: 0.9,
	locusEdgeFalloffMin: 0,
	centerRepelStrength: 0.016,
	centerRepelFalloff: 1.1,
	centerOrbitPlateau: 0.15,
	lociWeights: [
		{ type: 'attract', weight: 0.5 },
		{ type: 'repel', weight: 1 },
		{ type: 'orbit', weight: 2 },
		{ type: 'dampen', weight: 1 },
	],
};

// ————————————————————————————————————————————————————————————————————
// Helpers
// ————————————————————————————————————————————————————————————————————

/**
 * Random float between min and max
 * @param {number} min
 * @param {number} max
 * @return {number}
 */
function rand(min, max) {
	return min + Math.random() * (max - min);
}


/**
 * Clamp value between min and max
 * @param {number} val
 * @param {number} min
 * @param {number} max
 * @return {number}
 */
function clamp(val, min, max) {
	return val < min ? min : val > max ? max : val;
}


/**
 * Uniform-in-area random distance within an annulus [innerR, outerR].
 * The sqrt keeps particle density even across the ring; when innerR is 0
 * this reduces to the solid-disc sqrt(random) * outerR distribution.
 * @param {number} innerR - Inner radius (void); 0 for a solid disc
 * @param {number} outerR - Outer radius
 * @return {number}
 */
function _annulusDist(innerR, outerR) {
	if (innerR <= 0) return Math.sqrt(Math.random()) * outerR;
	if (innerR >= outerR) return outerR;
	return Math.sqrt(innerR * innerR + Math.random() * (outerR * outerR - innerR * innerR));
}


// ————————————————————————————————————————————————————————————————————
// Layer state — holds per-layer simulation data
// ————————————————————————————————————————————————————————————————————

/**
 * Create a self-contained layer state object
 * @param {object} cfg - Merged config for this layer
 * @param {number} cx - Center X
 * @param {number} cy - Center Y
 * @param {number} radius - Circle radius
 * @param {number} w - Canvas CSS width
 * @param {number} h - Canvas CSS height
 * @return {object}
 */
function createLayer(cfg, cx, cy, canvasRadius, w, h) {
	const radius = canvasRadius * cfg.scale;
	const layer = { cfg, cx, cy, radius, w, h, frameCount: 0 };

	// Particles
	const count = _layerCount(cfg, radius);
	const seedInner = radius * cfg.respawnInnerRadiusFraction;
	layer.particles = new Array(count);
	for (let i = 0; i < count; i++) {
		const angle = Math.random() * Math.PI * 2;
		const dist = _annulusDist(seedInner, radius);
		layer.particles[i] = {
			x: cx + Math.cos(angle) * dist,
			y: cy + Math.sin(angle) * dist,
			vx: 0,
			vy: 0,
			r: cfg.minRadius,
			targetR: cfg.minRadius,
			// Start hidden and fade in after a random stagger so the field
			// assembles in a scattered order rather than snapping into view.
			scale: 0,
			delay: Math.random() * cfg.introMaxDelay,
			dying: false,
		};
	}

	// Loci
	_initLoci(layer);

	// Density grid
	const cells = cfg.densityGridCells;
	layer.densityCells = cells;
	layer.densityGrid = new Array(cells * cells);
	for (let i = 0; i < layer.densityGrid.length; i++) {
		layer.densityGrid[i] = [];
	}

	return layer;
}


/**
 * Particle count for a layer.
 * If cfg.particleCount is set, use it. Otherwise derive from the layer's
 * pixel diameter: 1 particle per px of diameter (so a 1000 px scale-1
 * layer ≈ 1000 particles, a 0.6-scale layer ≈ 600).
 */
function _layerCount(cfg, layerRadius) {
	if (cfg.particleCount != null) return cfg.particleCount;
	const mult = cfg.densityMultiplier != null ? cfg.densityMultiplier : 1;
	return Math.round(layerRadius * 2 * mult);
}


/**
 * Build the weighted type table and spawn initial loci for a layer
 */
function _initLoci(layer) {
	const cfg = layer.cfg;

	// Build cumulative weight table
	layer._lociTypeTable = [];
	let total = 0;
	for (const entry of cfg.lociWeights) {
		const w = entry.weight != null ? entry.weight : 1;
		if (w > 0) {
			total += w;
			layer._lociTypeTable.push({ type: entry.type, cumWeight: total });
		}
	}
	layer._lociWeightTotal = total;

	// Spawn with staggered ages
	layer.loci = [];
	for (let i = 0; i < cfg.locusCount; i++) {
		const l = _spawnLocus(layer);
		l.age = Math.random() * l.lifespan;
		layer.loci.push(l);
	}
}


/**
 * Pick a random locus type from the weighted table
 */
function _randomLocusType(layer) {
	const roll = Math.random() * layer._lociWeightTotal;
	for (const entry of layer._lociTypeTable) {
		if (roll <= entry.cumWeight) return entry.type;
	}
	return layer._lociTypeTable[layer._lociTypeTable.length - 1].type;
}


/**
 * Create a new locus with randomized arc geometry and strength
 */
function _spawnLocus(layer) {
	const cfg = layer.cfg;
	const r = layer.radius;
	const type = _randomLocusType(layer);

	const arcCenterAngle = Math.random() * Math.PI * 2;
	const arcCenterDist = rand(cfg.locusArcMinOffset, cfg.locusArcMaxOffset) * r;
	const semiMajor = rand(cfg.locusArcMinRadius, cfg.locusArcMaxRadius) * r;
	const semiMinor = rand(cfg.locusArcMinEccentricity, cfg.locusArcMaxEccentricity) * semiMajor;
	const rotation = Math.random() * Math.PI * 2;
	const startAngle = Math.random() * Math.PI * 2;
	const sweep = rand(cfg.locusArcMinSweep, cfg.locusArcMaxSweep);
	const lifespan = rand(cfg.locusMinLife, cfg.locusMaxLife);
	const peakStrength = rand(cfg.locusMinStrength, cfg.locusMaxStrength);
	const influence = rand(cfg.locusMinInfluence, cfg.locusMaxInfluence) * r;
	const orbitDir = Math.random() < 0.5 ? 1 : -1;

	return {
		type,
		orbitDir,
		acx: layer.cx + Math.cos(arcCenterAngle) * arcCenterDist,
		acy: layer.cy + Math.sin(arcCenterAngle) * arcCenterDist,
		semiMajor,
		semiMinor,
		rotation,
		startAngle,
		sweep,
		lifespan,
		peakStrength,
		influence,
		age: 0,
		x: 0,
		y: 0,
		strength: 0,
	};
}


// ————————————————————————————————————————————————————————————————————
// ParticleCloudCanvas class
// ————————————————————————————————————————————————————————————————————

export class ParticleCloudCanvas {

	/**
	 * @param {HTMLElement} container
	 * @param {object[]} layerConfigs - Array of per-layer option objects
	 * @param {object} globalOptions - Shared options (e.g. debug)
	 */
	constructor(container, layerConfigs, globalOptions = {}) {
		this.container = container;
		this.debug = globalOptions.debug || false;
		this.time = 0;
		this.running = false;
		this.rafId = null;

		this._initCanvas();

		this._layerOpts = layerConfigs;

		// Build layer state objects
		this.layers = layerConfigs.map(opts => {
			const cfg = Object.assign({}, LAYER_DEFAULTS, LAYER_OVERRIDES, opts);
			return createLayer(cfg, this.cx, this.cy, this.radius, this.w, this.h);
		});

		this._initObserver();
		this._start();
	}


	/**
	 * Create and size the canvas element
	 */
	_initCanvas() {
		const dpr = window.devicePixelRatio || 1;
		const rect = this.container.getBoundingClientRect();
		const w = rect.width;
		const h = rect.height;

		this.canvas = document.createElement('canvas');
		this.canvas.width = w * dpr;
		this.canvas.height = h * dpr;
		this.canvas.style.width = w + 'px';
		this.canvas.style.height = h + 'px';

		this.ctx = this.canvas.getContext('2d');
		this.ctx.scale(dpr, dpr);

		this.w = w;
		this.h = h;
		this.cx = w / 2;
		this.cy = h / 2;
		this.radius = Math.min(w, h) / 2;

		this.container.appendChild(this.canvas);
	}


	/**
	 * Set up IntersectionObserver to pause when not visible
	 */
	_initObserver() {
		this.observer = new IntersectionObserver(entries => {
			const visible = entries[0].isIntersecting;
			if (visible && !this.running) {
				this._start();
			} else if (!visible && this.running) {
				this._stop();
			}
		});
		this.observer.observe(this.container);
	}


	/**
	 * Start the animation loop
	 */
	_start() {
		this.running = true;
		this._lastTime = performance.now();
		this._frameDt = 0;
		this._tick();
	}


	/**
	 * Stop the animation loop
	 */
	_stop() {
		this.running = false;
		if (this.rafId) {
			cancelAnimationFrame(this.rafId);
			this.rafId = null;
		}
	}


	/**
	 * Animation frame tick — updates and draws all layers
	 */
	_tick() {
		if (!this.running) return;

		const now = performance.now();
		const dt = (now - this._lastTime) / 1000;
		this._lastTime = now;
		this.time += dt;
		this._frameDt = dt;

		// Single clear for all layers
		this.ctx.clearRect(0, 0, this.w, this.h);

		// Update and draw each layer
		for (let i = 0; i < this.layers.length; i++) {
			const layer = this.layers[i];
			layer.frameCount++;
			this._updateLoci(layer);

			if (layer.frameCount <= 1 || layer.frameCount % layer.cfg.densityUpdateInterval === 0) {
				this._updateDensityGrid(layer);
			}

			this._drawLayer(layer);
		}

		// Debug overlay (draws on top of all layers)
		if (this.debug) {
			for (let i = 0; i < this.layers.length; i++) {
				this._drawDebugLoci(this.layers[i]);
			}
		}

		this.rafId = requestAnimationFrame(() => this._tick());
	}


	/**
	 * Update locus positions, strengths, and lifecycle for a layer
	 */
	_updateLoci(layer) {
		const cfg = layer.cfg;
		const r = layer.radius;
		const dt = this._frameDt;

		for (let i = 0; i < layer.loci.length; i++) {
			let l = layer.loci[i];

			l.age += dt;

			if (l.age >= l.lifespan) {
				l = _spawnLocus(layer);
				layer.loci[i] = l;
			}

			const progress = l.age / l.lifespan;
			const envelope = Math.sin(progress * Math.PI);
			const rawStrength = l.peakStrength * envelope;
			l.strength = l.type === 'dampen'
				? rawStrength * cfg.dampenStrengthScale
				: rawStrength;

			const angle = l.startAngle + l.sweep * progress;
			const localX = Math.cos(angle) * l.semiMajor;
			const localY = Math.sin(angle) * l.semiMinor;

			const cosR = Math.cos(l.rotation);
			const sinR = Math.sin(l.rotation);
			let px = l.acx + localX * cosR - localY * sinR;
			let py = l.acy + localX * sinR + localY * cosR;

			const dx = px - layer.cx;
			const dy = py - layer.cy;
			const dist = Math.sqrt(dx * dx + dy * dy);
			if (dist > r) {
				const scale = r / dist;
				px = layer.cx + dx * scale;
				py = layer.cy + dy * scale;
			}

			l.x = px;
			l.y = py;

			const edgeFrac = dist / r;
			const edgeStart = cfg.locusEdgeFalloffStart;
			if (edgeFrac > edgeStart) {
				const span = 1 - edgeStart;
				const edgeFade = span > 0
					? Math.max(0, 1 - (edgeFrac - edgeStart) / span)
					: 0;
				const floor = cfg.locusEdgeFalloffMin;
				l.strength *= floor + (1 - floor) * edgeFade;
			}
		}
	}


	/**
	 * Rebuild the density grid with particle indices per cell
	 */
	_updateDensityGrid(layer) {
		const cells = layer.densityCells;
		const grid = layer.densityGrid;
		const cellW = layer.w / cells;
		const cellH = layer.h / cells;
		const particles = layer.particles;

		for (let i = 0; i < grid.length; i++) {
			grid[i].length = 0;
		}

		for (let i = 0; i < particles.length; i++) {
			const p = particles[i];
			const col = Math.floor(p.x / cellW);
			const row = Math.floor(p.y / cellH);
			if (col >= 0 && col < cells && row >= 0 && row < cells) {
				grid[row * cells + col].push(i);
			}
		}

		const cfg = layer.cfg;
		const dMin = cfg.densityMin;
		const dMax = cfg.densityMax;
		const rMin = cfg.minRadius;
		const rMax = cfg.maxRadius;
		const rRange = rMax - rMin;
		const dRange = dMax - dMin;

		for (let i = 0; i < particles.length; i++) {
			const p = particles[i];
			const col = Math.floor(p.x / cellW);
			const row = Math.floor(p.y / cellH);
			if (col >= 0 && col < cells && row >= 0 && row < cells) {
				const density = grid[row * cells + col].length;
				if (density <= dMin) {
					p.targetR = rMin;
				} else if (density >= dMax) {
					p.targetR = rMax;
				} else {
					p.targetR = rMin + rRange * ((density - dMin) / dRange);
				}
			}
		}
	}


	/**
	 * Update particle positions and draw a single layer's particles
	 */
	_drawLayer(layer) {
		const ctx = this.ctx;
		const cfg = layer.cfg;
		const particles = layer.particles;
		const cx = layer.cx;
		const cy = layer.cy;
		const r = layer.radius;
		const t = this.time;

		ctx.fillStyle = cfg.color;

		const noiseScale = cfg.noiseScale;
		const noiseSpeed = cfg.noiseSpeed;
		const noiseAmp = cfg.baseVelocity;
		const speedMult = cfg.speed;
		const dt = this._frameDt;
		const lerpAlpha = Math.min(1, cfg.radiusLerpSpeed * dt);
		const bStart = cfg.boundaryForceStart * r;
		const bStrength = cfg.boundaryForceStrength;
		const crStrength = cfg.centerRepelStrength;
		const crFalloff = cfg.centerRepelFalloff * r;
		const coStrength = cfg.centerOrbitStrength;
		const coFalloff = cfg.centerOrbitFalloff * r;
		const coPlateau = clamp(cfg.centerOrbitPlateau, 0, 1);
		const sepRadius = cfg.separationRadius;
		const sepStrength = cfg.separationStrength;
		const sepEnabled = sepRadius > 0 && sepStrength > 0;
		const lifeDur = cfg.particleFadeDuration;
		const lifeStep = lifeDur > 0 ? dt / lifeDur : 1;
		const respawnRadius = r * cfg.respawnOuterRadiusFraction;
		const respawnInnerRadius = r * cfg.respawnInnerRadiusFraction;
		const edgeFadeSpan = clamp(cfg.edgeFadeWidth, 0, r);
		const edgeFadeStartDist = r - edgeFadeSpan;
		const gridCells = layer.densityCells;
		const grid = layer.densityGrid;
		const gridCellW = layer.w / gridCells;
		const gridCellH = layer.h / gridCells;

		for (let i = 0; i < particles.length; i++) {
			const p = particles[i];

			// Distance from center — used to gate locus influence (particles
			// past the edge are written off; loci do not pull them back) and
			// later for the optional inward boundary push.
			const pdx = p.x - cx;
			const pdy = p.y - cy;
			const pdist = Math.sqrt(pdx * pdx + pdy * pdy);
			const inside = pdist <= r;

			// Curl noise
			const ns = noiseScale;
			const tv1 = t * noiseSpeed;
			const tv2 = t * noiseSpeed * 0.7;
			const tv3 = t * noiseSpeed * 1.3;
			const xyHalf = (p.x + p.y) * ns * 0.5;

			let fx = (-Math.sin(p.y * ns + tv2) * ns
				+ Math.cos(xyHalf + tv3) * ns * 0.5) * noiseAmp;
			let fy = -(Math.cos(p.x * ns + tv1) * ns
				+ Math.cos(xyHalf + tv3) * ns * 0.5) * noiseAmp;

			// Locus influences — only applied while the particle is inside
			// the circle. Past the edge, the particle is committed to fading
			// out; loci no longer try to drag it back.
			if (inside) for (let j = 0; j < layer.loci.length; j++) {
				const l = layer.loci[j];
				const ldx = p.x - l.x;
				const ldy = p.y - l.y;
				const ldist = Math.sqrt(ldx * ldx + ldy * ldy);

				if (ldist < l.influence && ldist > 0.01) {
					const falloff = 1 - ldist / l.influence;
					const s = l.strength * falloff;

					switch (l.type) {
						case 'attract':
							fx -= (ldx / ldist) * s;
							fy -= (ldy / ldist) * s;
							break;
						case 'repel':
							fx += (ldx / ldist) * s;
							fy += (ldy / ldist) * s;
							break;
						case 'orbit':
							fx += (-ldy / ldist) * s * l.orbitDir;
							fy += (ldx / ldist) * s * l.orbitDir;
							break;
						case 'dampen':
							fx *= (1 - s);
							fy *= (1 - s);
							break;
					}
				}
			}

			// Particle separation
			if (sepEnabled) {
				const col = Math.floor(p.x / gridCellW);
				const row = Math.floor(p.y / gridCellH);
				const rMin = Math.max(0, row - 1);
				const rMax = Math.min(gridCells - 1, row + 1);
				const cMin = Math.max(0, col - 1);
				const cMax = Math.min(gridCells - 1, col + 1);

				for (let gr = rMin; gr <= rMax; gr++) {
					for (let gc = cMin; gc <= cMax; gc++) {
						const cell = grid[gr * gridCells + gc];
						for (let k = 0; k < cell.length; k++) {
							const ni = cell[k];
							if (ni === i) continue;
							const n = particles[ni];
							const sdx = p.x - n.x;
							const sdy = p.y - n.y;
							const sDist = Math.sqrt(sdx * sdx + sdy * sdy);
							if (sDist < sepRadius && sDist > 0.01) {
								const repel = sepStrength * (1 - sDist / sepRadius);
								fx += (sdx / sDist) * repel;
								fy += (sdy / sDist) * repel;
							}
						}
					}
				}
			}

			// Center repulsion
			const cdx = p.x - cx;
			const cdy = p.y - cy;
			const cdist = Math.sqrt(cdx * cdx + cdy * cdy);
			if (cdist > 0.01 && cdist < crFalloff) {
				const crForce = crStrength * (1 - cdist / crFalloff);
				fx += (cdx / cdist) * crForce;
				fy += (cdy / cdist) * crForce;
			}

			// Center orbit — uniform angular velocity inside the plateau, then falls off.
			// Linear force scales with distance up to coPlateau of the falloff radius
			// (so a particle at the very center isn't whipped around faster than its
			// neighbors), then quadratically fades to zero by the falloff edge.
			if (cdist > 0.01 && cdist < coFalloff) {
				const t = cdist / coFalloff;
				const innerScale = coPlateau > 0 && t < coPlateau ? t / coPlateau : 1;
				const outerFade = t < coPlateau || coPlateau >= 1
					? 1
					: 1 - ((t - coPlateau) / (1 - coPlateau)) * ((t - coPlateau) / (1 - coPlateau));
				const coForce = coStrength * innerScale * outerFade;
				fx += (-cdy / cdist) * coForce;
				fy += (cdx / cdist) * coForce;
			}

			// Optional soft inward push — disabled by default. The circle
			// is now a soft fade boundary rather than a wall, but a layer
			// can re-enable a gentle return force by setting
			// boundaryForceStrength > 0.
			if (bStrength > 0 && cdist > bStart) {
				const over = (cdist - bStart) / (r - bStart);
				const bForce = bStrength * over * over;
				fx -= (cdx / cdist) * bForce;
				fy -= (cdy / cdist) * bForce;
			}

			// Update position (speed scales the total per-frame displacement)
			p.x += fx * speedMult;
			p.y += fy * speedMult;

			// Re-measure distance after movement
			const newDx = p.x - cx;
			const newDy = p.y - cy;
			const newDist = Math.sqrt(newDx * newDx + newDy * newDy);

			// Lifecycle scale (multiplier on visible diameter, 0–1).
			// Particles that cross the boundary are committed to dying and
			// shrink one-way to zero, even if curl noise drifts them back
			// inside. At scale 0 they respawn at the center and grow back
			// to full size over particleFadeDuration.
			if (newDist > r && !p.dying) {
				p.dying = true;
			}

			if (p.delay > 0) {
				// Intro stagger: stay hidden while the flow keeps moving, so
				// the particle fades in mid-motion rather than from a freeze.
				p.delay -= dt;
			} else if (p.dying) {
				p.scale = Math.max(0, p.scale - lifeStep);
				if (p.scale <= 0) {
					const a = Math.random() * Math.PI * 2;
					const d = _annulusDist(respawnInnerRadius, respawnRadius);
					p.x = cx + Math.cos(a) * d;
					p.y = cy + Math.sin(a) * d;
					p.r = cfg.minRadius;
					p.targetR = cfg.minRadius;
					p.scale = 0;
					p.dying = false;
				}
			} else if (p.scale < 1) {
				p.scale = Math.min(1, p.scale + lifeStep);
			}

			// Lerp radius (density-driven base size)
			p.r += (p.targetR - p.r) * lerpAlpha;

			// Edge fade — geometric shrink to zero across the band just
			// inside the true edge, so a particle is fully invisible before
			// it reaches the circular clip mask (no mid-body cropping).
			let edgeScale = 1;
			if (edgeFadeSpan > 0 && newDist > edgeFadeStartDist) {
				edgeScale = (r - newDist) / edgeFadeSpan;
				if (edgeScale < 0) edgeScale = 0;
			}

			// Draw — visible diameter is base radius * lifecycle scale,
			// clamped by the edge fade so it never exceeds the boundary.
			const drawScale = edgeScale < p.scale ? edgeScale : p.scale;
			if (drawScale <= 0) continue;
			ctx.beginPath();
			ctx.arc(p.x, p.y, p.r * drawScale, 0, Math.PI * 2);
			ctx.fill();
		}
	}


	/**
	 * Draw debug overlay for a layer's loci
	 */
	_drawDebugLoci(layer) {
		const ctx = this.ctx;
		const colors = {
			attract: 'rgba(0, 180, 80, 0.2)',
			repel: 'rgba(220, 40, 40, 0.2)',
			orbit: 'rgba(40, 120, 220, 0.2)',
			dampen: 'rgba(180, 140, 40, 0.2)',
		};
		const strokeColors = {
			attract: 'rgba(0, 180, 80, 0.5)',
			repel: 'rgba(220, 40, 40, 0.5)',
			orbit: 'rgba(40, 120, 220, 0.5)',
			dampen: 'rgba(180, 140, 40, 0.5)',
		};

		for (let i = 0; i < layer.loci.length; i++) {
			const l = layer.loci[i];

			ctx.beginPath();
			ctx.arc(l.x, l.y, l.influence, 0, Math.PI * 2);
			ctx.fillStyle = colors[l.type];
			ctx.fill();
			ctx.strokeStyle = strokeColors[l.type];
			ctx.lineWidth = 1;
			ctx.stroke();

			ctx.beginPath();
			ctx.arc(l.x, l.y, 3, 0, Math.PI * 2);
			ctx.fillStyle = strokeColors[l.type];
			ctx.fill();

			ctx.fillStyle = strokeColors[l.type];
			ctx.font = '10px sans-serif';
			ctx.fillText(l.type, l.x + 6, l.y - 6);
		}
	}


	/**
	 * Re-create all layers with overrides merged into defaults.
	 * Per-layer opts (e.g. color, scale from YAML) still take precedence.
	 * @param {object} overrides
	 */
	applyOverrides(overrides) {
		this._stop();
		this.time = 0;
		this.layers = this._layerOpts.map(opts => {
			const cfg = Object.assign({}, LAYER_DEFAULTS, LAYER_OVERRIDES, overrides, opts);
			return createLayer(cfg, this.cx, this.cy, this.radius, this.w, this.h);
		});
		this._start();
	}


	/**
	 * Toggle the debug overlay (loci position, influence radius, type label)
	 * @param {boolean} on
	 */
	setDebug(on) {
		this.debug = !!on;
	}


	/**
	 * Reset all layers as if just loaded
	 */
	reset() {
		this._stop();
		this.time = 0;

		this.layers = this.layers.map(layer => {
			return createLayer(layer.cfg, this.cx, this.cy, this.radius, this.w, this.h);
		});

		this._start();
	}


	/**
	 * Clean up — cancel animation, disconnect observer, remove canvas
	 */
	destroy() {
		this._stop();
		if (this.observer) {
			this.observer.disconnect();
			this.observer = null;
		}
		if (this.canvas && this.canvas.parentNode) {
			this.canvas.parentNode.removeChild(this.canvas);
		}
		this.canvas = null;
		this.ctx = null;
		this.layers = null;
	}
}
